import React from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Facebook from '@mui/icons-material/Facebook';
import Instagram from '@mui/icons-material/Instagram';
import LinkedIn from '@mui/icons-material/LinkedIn';
import Twitter from '@mui/icons-material/Twitter';
import YouTube from '@mui/icons-material/YouTube';
import useMediaQuery from "@mui/material/useMediaQuery";
import laboratoriaLogo from '@laboratoria/sdk-react/src/icons/laboratoria-logo.svg';
import { registerMessages } from '../../intl';
import es from './intl/es';
import pt from './intl/pt';

registerMessages('footer', { es, pt });

const Footer = ({ lang }) => {
  const isSm = useMediaQuery('(max-width:900px)');
  const urls = (
    lang === 'pt'
      ? {
        contact: 'https://www.laboratoria.la/br/entre-em-contato',
        blog: 'https://hub.laboratoria.la/br?utm_source=my-laboratoria&utm_medium=footer',
        privacy: 'https://www.laboratoria.la/privacidade',
        terms: 'https://www.laboratoria.la/termos',
        hire: 'https://talento.laboratoria.la/br?utm_source=my-laboratoria&utm_medium=footer',
        workWithUs: 'https://www.laboratoria.la/br/trabalhe-na-laboratoria',
      }
      : {
        contact: 'https://www.laboratoria.la/contactanos',
        blog: 'https://hub.laboratoria.la/?utm_source=my-laboratoria&utm_medium=footer',
        privacy: 'https://www.laboratoria.la/privacy',
        terms: 'https://www.laboratoria.la/terminosycondiciones',
        hire: 'https://talento.laboratoria.la/?utm_source=my-laboratoria&utm_medium=footer',
        workWithUs: 'https://www.laboratoria.la/trabaja-con-nosotros',
      }
  );

  const sx = theme => ({
    mt: 1,
    backgroundColor: '#000',
    borderRadius: '10px 10px 0 0',
    paddingTop: {
      xs: 4,
      md: 4,
    },
    paddingBottom: {
      xs: 4,
      sm: 4,
      md: 6,
    },
    paddingRight: {
      xs: 10,
      sm: 3,
      md: 6,
    },
    paddingLeft: {
      xs: 10,
      sm: 3,
      md: 6,
    },
    color: '#fff',
    '& img': {
      filter: 'invert(1)',
      width: '155px',
    },
    '& a': {
      textDecoration: 'none',
    },
    '& h4': {
      marginBottom: 3,
    },
    '& .MuiTypography-body2': {
      paddingY: 1,
      fontSize: { xs: '0.875rem', sm: '0.75rem', md: '1.063rem' },
    },
    '& .MuiTypography-body2:first-of-type': {
      paddingTop: 0,
    },
    '& .copyright': {
      marginTop: 6,
      textAlign: { xs: 'center', md: 'left' },
    },
    '& .MuiGrid-root > .MuiGrid-item': {
      paddingLeft: { xs: 0, sm: 2 },
      marginX: 'auto',
    },
  });

  return (
    <>
      <Container maxWidth="lg" sx={sx} disableGutters={false}>
        <Grid container spacing={isSm ? 2 : 2}>
          <Grid item
            xs={11}
            sm={5}
            md={5}
            sx={{
              textAlign: {
                xs: 'center',
                sm: 'left',
              },
            }}>
            <img src={laboratoriaLogo} alt="logo" />
            <Typography variant="body1"
              sx={{
                fontSize: { xs: '0.875rem', sm: '0.75rem', md: '1.063rem' },
                fontWeight: { xs: '300', sm: '400' },
                paddingY: { xs: 2 },
              }}
            >
              <FormattedMessage id="footer/slogan" />
            </Typography>
            <Typography variant="h4"
              sx={{
                fontFamily: 'Open Sans',
                fontSize: { xs: '0.875rem', sm: '0.75rem', md: '1.063rem' },
                fontWeight: '600'
              }}>
              <FormattedMessage id="footer/follow-us" />
            </Typography>
            <Box
              sx={{
                marginBottom: 2,
                '& svg': {
                  marginX: '0.5rem',
                },
              }}
            >
              {lang === 'pt'
                ? (
                  <>
                    <a href="https://www.facebook.com/laboratoriabr/">
                      <Facebook color="white" />
                    </a>
                    <a href="https://www.instagram.com/laboratoria_br/">
                      <Instagram color="white" />
                    </a>
                    <a href="https://www.linkedin.com/company/laboratoria-brasil/">
                      <LinkedIn color="white" />
                    </a>
                    <a href="https://twitter.com/Laboratoriala">
                      <Twitter color="white" />
                    </a>
                    <a href="https://www.youtube.com/@LaboratoriaOficial">
                      <YouTube color="white" />
                    </a>
                  </>
                )
                : (
                  <>
                    <a href="https://www.facebook.com/laboratoriala/">
                      <Facebook color="white" />
                    </a>
                    <a href="https://www.instagram.com/laboratoriala/">
                      <Instagram color="white" />
                    </a>
                    <a href="https://www.linkedin.com/school/laboratoria/">
                      <LinkedIn color="white" />
                    </a>
                    <a href="https://twitter.com/Laboratoriala">
                      <Twitter color="white" />
                    </a>
                    <a href="https://www.youtube.com/@LaboratoriaOficial">
                      <YouTube color="white" />
                    </a>
                  </>
                )
              }
            </Box>
          </Grid>
          <Grid item md={1} sm={1} />
          <Grid item
            xs={11}
            sm={3}
            md={3}
            sx={{
              textAlign: {
                xs: 'center',
                sm: 'left'
              }
            }}>
            <Typography variant="h4"
              sx={{
                textAlign: {
                  xs: 'center',
                  sm: 'left'
                },
                fontFamily: 'Open Sans',
                fontWeight: '600',
                fontSize: { xs: '1.125rem', sm: '0.875rem', md: '1.25rem' },
                textTransform: 'uppercase',
              }}
            >
              <FormattedMessage id="footer/learn-more" />
            </Typography>
            <Typography variant="body2">
              <a href={urls.contact}>
                <FormattedMessage id="footer/contact" />
              </a>
            </Typography>
            <Typography variant="body2">
              <a href={urls.blog}>
                <FormattedMessage id="footer/blog" />
              </a>
            </Typography>
            <Typography variant="body2">
              <a href={urls.privacy}>
                <FormattedMessage id="footer/privacy" />
              </a>
            </Typography>
            <Typography variant="body2">
              <a href={urls.terms}>
                <FormattedMessage id="footer/terms" />
              </a>
            </Typography>
          </Grid>
          <Grid item
            xs={11}
            sm={3}
            md={3}
            sx={{
              textAlign: {
                xs: 'center',
                sm: 'left',
              },
            }}>
            <Typography variant="h4"
              sx={{
                textAlign: {
                  xs: 'center',
                  sm: 'left'
                },
                fontFamily: 'Open Sans',
                fontWeight: '600',
                fontSize: { xs: '1.125rem', sm: '0.875rem', md: '1.25rem' },
                textTransform: 'uppercase',
              }}
            >
              <FormattedMessage id="footer/get-involved" />
            </Typography>
            <Typography variant="body2">
              <Link to={`/${lang}/`}>
                <FormattedMessage id="footer/apply" />
              </Link>
            </Typography>
            <Typography variant="body2">
              <a href={urls.hire}>
                <FormattedMessage id="footer/hire" />
              </a>
            </Typography>
            <Typography variant="body2">
              <a href={urls.workWithUs}>
                <FormattedMessage id="footer/work-with-us" />
              </a>
            </Typography>
          </Grid>
        </Grid>
        <Grid container spacing={isSm ? 2 : 2} >
          <Typography variant="subtitle2" className="copyright"
            sx={{
              paddingLeft: { xs: '0px', sm: 2 },
              textAlign: {
                xs: 'center',
                sm: 'left',
              },
            }}
          >
            Copyright © {(new Date()).getFullYear()} Laboratoria.
            {' '}
            <FormattedMessage id="footer/copyright" />
          </Typography>
        </Grid>
      </Container>
    </>
  );
};

export default Footer;